import { toast } from "@/components/ui";
import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { AllergyIntolerance } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";
import type { AllergyIntoleranceTypeValue } from "../types/allergies";

export enum AllergyStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export enum Action {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  RESOLVED = "RESOLVED",
  VOID = "VOID",
  CONFIRM = "CONFIRM",
  DENY = "DENY",
}

interface AvailableAction {
  action: Action;
  unavailableReason?: string;
}

export interface PatientAllergy {
  allergyIntolerance: AllergyIntolerance;
  needsConfirmation: boolean;
  availableStatusChangeActions: AvailableAction[];
  availableConfirmationActions: AvailableAction[];
}

export interface PatientAllergiesResponse {
  nka: boolean;
  nkaNeedsConfirmation: boolean;
  notCharted: boolean;
  allergies: PatientAllergy[];
}

interface GetPatientAllergiesParams {
  patientId?: string;
  status?: AllergyStatus;
}

export const usePatientAllergies = ({
  patientId,
  status = AllergyStatus.ACTIVE,
}: GetPatientAllergiesParams) => {
  return useApi<PatientAllergiesResponse>({
    queryOptions: {
      enabled: Boolean(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get<PatientAllergiesResponse>(
          `/rx/allergies/patient/${patientId}`,
          {
            params: {
              status,
            },
          },
        );
        return response?.data;
      },
      select: (data) => {
        return {
          nka: data.nka,
          nkaNeedsConfirmation: data.nkaNeedsConfirmation,
          notCharted: data.notCharted,
          allergies: data.allergies?.map((allergy) => {
            return {
              allergyIntolerance: new AllergyIntolerance(
                allergy.allergyIntolerance,
              ),
              needsConfirmation: allergy.needsConfirmation,
              availableStatusChangeActions:
                allergy.availableStatusChangeActions,
              availableConfirmationActions:
                allergy.availableConfirmationActions,
            };
          }),
        };
      },
      queryKey: QueryKeys.Patient.MedicalRecords.Allergies.STATUS_SECTION(
        status,
        patientId,
      ),
    },
  });
};

export const useBatchConfirmAllergies = ({
  patientId,
  onSuccess,
}: {
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateAllergyResponse, string[]>({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Allergies.BATCH_CONFIRM(patientId),
      mutationFn: async (allergyIntoleranceIds) => {
        const response = await axiosInstance.put(
          "/rx/allergies/batch-confirm",
          {
            allergyIntoleranceIds,
          },
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Allergies.STATUS_SECTION(
            AllergyStatus.ACTIVE,
            patientId,
          ),
        });
        onSuccess?.();
      },
    },
  });
};

export interface AllergySearchItem {
  allergyName: string;
  allergyCode: string;
  allergyCodeSystem: string;
}

interface AllergenSearchResult {
  name: string;
  brandName: string | null;
  allergenId: string;
  system: string;
}

interface AllergenSearchResponse {
  allergens: AllergenSearchResult[];
}

const mapAllergenToSearchItem = (
  allergen: AllergenSearchResult,
): AllergySearchItem => ({
  allergyName: allergen.name,
  allergyCode: allergen.allergenId,
  allergyCodeSystem: allergen.system,
});

export const useSearchAllergies = (search?: string) => {
  return useApi<AllergenSearchResponse, AllergySearchItem[]>({
    queryOptions: {
      enabled: !!(search && search.length > 2),
      queryKey: QueryKeys.Reference.Allergens.SEARCH(search),
      queryFn: async () => {
        const response = await axiosInstance.get("/rx/allergies/search", {
          params: {
            searchTerm: search,
          },
        });
        return response?.data;
      },
      select: (data) => {
        return data?.allergens?.map(mapAllergenToSearchItem) || [];
      },
      staleTime: Number.POSITIVE_INFINITY,
    },
  });
};

export const useCommonAllergies = () => {
  return useApi<AllergenSearchResponse, AllergySearchItem[]>({
    queryOptions: {
      queryKey: QueryKeys.Reference.Allergens.COMMON,
      queryFn: async () => {
        const response = await axiosInstance.get("/rx/allergies/common");
        return response?.data;
      },
      select: (data) => {
        return data?.allergens?.map(mapAllergenToSearchItem) || [];
      },
      staleTime: Number.POSITIVE_INFINITY,
    },
  });
};

interface CreateAllergyParams {
  patientId: string;
  onSuccess?: () => void;
}

interface ReactionCriteria {
  id?: string;
  severity?: string;
  description?: string;
}

interface CreateAllergyCriteria {
  allergyCode: string;
  allergyCodeSystem: string;
  allergyName: string;
  allergyType: AllergyIntoleranceTypeValue;
  note?: string;
  encounterId?: string;
  reactions?: ReactionCriteria[];
}

interface CreateAllergyResponse {
  bundle: object;
}

export const useCreateAllergy = ({
  patientId,
  onSuccess,
}: CreateAllergyParams) => {
  const queryClient = useQueryClient();
  return useApiMutation<
    CreateAllergyResponse,
    CreateAllergyCriteria,
    { detail: string }
  >({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Allergies.CREATE(patientId),
      mutationFn: async (criteria) => {
        const response = await axiosInstance.post("/rx/allergies/", {
          patientId: patientId,
          code: {
            text: criteria.allergyName,
            coding: [
              {
                system: criteria.allergyCodeSystem,
                code: criteria.allergyCode,
                display: criteria.allergyName,
              },
            ],
          },
          allergyType: criteria.allergyType,
          note: criteria.note,
          encounterId: criteria.encounterId,
          reactions: criteria.reactions,
        });
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Allergies.DEFAULT(patientId),
        });
        onSuccess?.();
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          toast.error(error.response?.data?.detail);
        } else {
          toast.error("Failed to create allergy");
        }
      },
    },
  });
};

interface UpdateAllergyParams {
  patientId: string;
  allergyIntoleranceId: string;
  onSuccess?: () => void;
  onError?: ({
    message,
  }: {
    message: string;
  }) => void;
}

interface UpdateAllergyCriteria {
  allergyType: AllergyIntoleranceTypeValue;
  note?: string;
  reactions?: ReactionCriteria[];
}

interface UpdateAllergyResponse {
  bundle: object;
}

export const useUpdateAllergy = ({
  patientId,
  allergyIntoleranceId,
  onSuccess,
}: UpdateAllergyParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateAllergyResponse, UpdateAllergyCriteria>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Allergies.UPDATE(
        patientId,
        allergyIntoleranceId,
      ),
      mutationFn: async (criteria) => {
        const response = await axiosInstance.patch(
          `/rx/allergies/${allergyIntoleranceId}`,
          {
            allergyType: criteria.allergyType,
            note: criteria.note,
            reactions: criteria.reactions,
          },
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Allergies.DEFAULT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};

export const useMarkAllergyActive = ({
  patientId,
  allergyIntoleranceId,
  onSuccess,
  onError,
}: UpdateAllergyParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateAllergyResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Allergies.ACTIVATE(
        patientId,
        allergyIntoleranceId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/allergies/${allergyIntoleranceId}/activate`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Allergies.DEFAULT(patientId),
        });
        onSuccess?.();
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          onError?.({
            message:
              "Patient has active NKDA. Please mark it as resolved first.",
          });
        }
      },
    },
  });
};

export const useMarkAllergyInactive = ({
  patientId,
  allergyIntoleranceId,
  onSuccess,
}: UpdateAllergyParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateAllergyResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Allergies.INACTIVATE(
        patientId,
        allergyIntoleranceId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/allergies/${allergyIntoleranceId}/inactivate`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Allergies.DEFAULT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};

export const useMarkAllergyResolved = ({
  patientId,
  allergyIntoleranceId,
  onSuccess,
}: UpdateAllergyParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateAllergyResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Allergies.RESOLVE(
        patientId,
        allergyIntoleranceId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/allergies/${allergyIntoleranceId}/resolve`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Allergies.DEFAULT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};

export const useMarkAllergyVoided = ({
  patientId,
  allergyIntoleranceId,
  onSuccess,
}: UpdateAllergyParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateAllergyResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Allergies.VOID(
        patientId,
        allergyIntoleranceId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/allergies/${allergyIntoleranceId}/void`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Allergies.DEFAULT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};

export const useMarkAllergyStatus = ({
  patientId,
  allergyIntoleranceId,
  onSuccess,
  onError,
}: {
  patientId: string;
  allergyIntoleranceId: string;
  onSuccess?: () => void;
  onError?: ({
    message,
  }: {
    message: string;
  }) => void;
}) => {
  const { isPending: isMarkingActive, mutate: markAllergyActive } =
    useMarkAllergyActive({
      patientId,
      allergyIntoleranceId,
      onSuccess,
      onError,
    });
  const { isPending: isMarkingInactive, mutate: markAllergyInactive } =
    useMarkAllergyInactive({
      patientId,
      allergyIntoleranceId,
      onSuccess,
      onError,
    });
  const { isPending: isMarkingResolved, mutate: markAllergyResolved } =
    useMarkAllergyResolved({
      patientId,
      allergyIntoleranceId,
      onSuccess,
      onError,
    });
  const { isPending: isMarkingVoided, mutate: markAllergyVoided } =
    useMarkAllergyVoided({
      patientId,
      allergyIntoleranceId,
      onSuccess,
      onError,
    });

  return {
    isSavingStatus:
      isMarkingActive ||
      isMarkingInactive ||
      isMarkingResolved ||
      isMarkingVoided,
    markAllergyActive,
    markAllergyInactive,
    markAllergyResolved,
    markAllergyVoided,
  };
};

interface MarkPatientNKDAParams {
  patientId: string;
  onSuccess?: () => void;
  onError?: ({
    message,
  }: {
    message: string;
  }) => void;
}

interface MarkPatientNKDAResponse {
  bundle: object;
}

export const useMarkPatientNKDA = ({
  patientId,
  onSuccess,
  onError,
}: MarkPatientNKDAParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<MarkPatientNKDAResponse, void>({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Allergies.MARK_NKDA(patientId),
      mutationFn: async () => {
        const response = await axiosInstance.post(
          `/rx/allergies/patient/${patientId}/nka`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Allergies.DEFAULT(patientId),
        });
        onSuccess?.();
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          onError?.({
            message: "Patient has active allergies. Please resolve them first.",
          });
        }
      },
    },
  });
};

export enum AllergyChartingAction {
  DOCUMENT_ALLERGY = "DOCUMENT_ALLERGY",
  CONFIRM_ALL_ALLERGIES = "CONFIRM_ALL_ALLERGIES",
}

export enum AllergyChartingActionNotAvailableReason {
  NO_PRIVILEGES = "NO_PRIVILEGES",
}

interface AvailableChartingAction {
  action: AllergyChartingAction;
  unavailableReason?: AllergyChartingActionNotAvailableReason;
}

interface GetAvailableAllergyActionsResponse {
  availableChartingActions: AvailableChartingAction[];
}

export const useGetAllergyChartingActions = (patientId: string) => {
  return useApi<GetAvailableAllergyActionsResponse, AvailableChartingAction[]>({
    queryOptions: {
      enabled: true,
      queryKey:
        QueryKeys.Patient.MedicalRecords.Allergies.AVAILABLE_CHARTING_ACTIONS(
          patientId,
        ),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/rx/allergies/available-actions",
        );
        return response?.data;
      },
      select: (data) => {
        return data?.availableChartingActions;
      },
      staleTime: Number.POSITIVE_INFINITY,
    },
  });
};

interface LastUpdateAgentResponse {
  resourceType: string;
  resourceId: string;
  lastUpdatedAt: string;
  agentReference: string;
  agentDisplay: string;
}

export const useAllergyLastUpdateAgent = ({
  patientId,
  allergyIntoleranceId,
  enabled,
}: {
  patientId: string;
  allergyIntoleranceId: string;
  enabled: boolean;
}) => {
  return useApi<LastUpdateAgentResponse>({
    queryOptions: {
      enabled,
      queryKey: QueryKeys.Patient.MedicalRecords.Allergies.LAST_UPDATE_AGENT(
        patientId,
        allergyIntoleranceId,
      ),
      queryFn: async () => {
        const response = await axiosInstance.get<LastUpdateAgentResponse>(
          `/charting/audit/last-update-agent/AllergyIntolerance/${allergyIntoleranceId}`,
        );
        const data = response?.data;
        // Ensure the UTC timestamp is treated as UTC by JS (append Z if missing)
        if (data?.lastUpdatedAt && !data.lastUpdatedAt.endsWith("Z")) {
          data.lastUpdatedAt = `${data.lastUpdatedAt}Z`;
        }
        return data;
      },
      staleTime: Number.POSITIVE_INFINITY,
    },
  });
};

export const useConfirmAllergy = ({
  patientId,
  allergyIntoleranceId,
  onSuccess,
}: UpdateAllergyParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateAllergyResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Allergies.CONFIRM(
        patientId,
        allergyIntoleranceId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/allergies/${allergyIntoleranceId}/confirm`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Allergies.DEFAULT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};
